/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import Button from '@atlaskit/button';
import { cssMap, jsx } from '@atlaskit/css';
import Popup from '@atlaskit/popup';
import { layers } from '@atlaskit/theme/constants';
import * as colors from '@atlaskit/theme/colors';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import WarningIcon from '@atlaskit/icon/core/migration/warning';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { type Integration, ScopeState } from '../../types';
import { InstrumentedButton } from '../../analytics/InstrumentedButton';
import { useAnalytics } from '../../analytics';
import { messages } from '../i18n';
import { GOOGLE_SERVICE, MICROSOFT_SERVICE, PLATFORM_SLACK_SERVICE } from './constants';
import { useThirdPartyState } from './ThirdPartyContext';
import { ThirdPartyAnalytics } from './ThirdPartyAnalytics';
import { useInvitePeopleContext } from '../InvitePeople';

const styles = cssMap({
	wrapper: {
		marginTop: token('space.100', '8px'),
		font: token(
			'font.body',
			'normal 400 14px/20px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, system-ui, "Helvetica Neue", sans-serif',
		),
	},
	popupWrapper: {
		position: 'relative',
		width: '330px',
		marginBlock: token('space.200', '16px'),
		marginInline: token('space.200', '16px'),
		font: token(
			'font.body',
			'normal 400 14px/20px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, system-ui, "Helvetica Neue", sans-serif',
		),
	},
	popupBody: {
		marginBlock: token('space.100', '8px'),
	},
	popupButtonWrapper: {
		borderRadius: token('border.radius.050', '2px'),
		cursor: 'pointer',
	},
	buttonTitle: {
		paddingInline: token('space.050', '4px'),
		display: 'inline-flex',
		textAlign: 'left',
		whiteSpace: 'initial',
	},
});

export interface OwnProps {
	scope: ScopeState;
}

const UpdateIntegration: React.FC<{
	integration: Integration;
	scopeState: ScopeState;
}> = ({ integration, scopeState }) => {
	const analytics = useAnalytics(ThirdPartyAnalytics);
	const { formatMessage } = useIntl();
	const { orgId } = useInvitePeopleContext();
	const { onConnectProvider, onDisconnectProvider } = useThirdPartyState();
	const [showPopup, setShowPopup] = useState(false);
	const [hover, setHover] = useState(false);

	let product: string | null = null;
	let popupTitle: string | null = null;
	let popupBody: string | null = null;
	let buttonTitle: string | null = null;
	let space: string | null = null;

	const iconColor = {
		error: {
			color: token('color.icon.danger', colors.R400),
			accent: token('color.icon.danger', colors.R300),
		},
		warning: {
			color: token('color.icon.danger', colors.Y300),
			accent: token('color.icon.danger', colors.Y200),
		},
	};

	switch (integration) {
		case PLATFORM_SLACK_SERVICE:
			product = formatMessage(messages.slackThirdPartyButton);
			break;
		case GOOGLE_SERVICE:
			product = formatMessage(messages.googleThirdPartyButton);
			space = formatMessage(messages.googleSpace);
			break;
		case MICROSOFT_SERVICE:
			product = formatMessage(messages.microsoftThirdPartyButton);
			break;
	}

	// No scope messaging more general, will replace detailed partial scope messaging
	if (scopeState === ScopeState.NO_SCOPE) {
		if (integration === PLATFORM_SLACK_SERVICE) {
			popupTitle = formatMessage(messages.reviewConnectionTitle, {
				integration: product,
			});
			popupBody = formatMessage(messages.reviewConnectionDescription, {
				integration: product,
			});
			buttonTitle = formatMessage(messages.reviewConnection, {
				integration: product,
			});
		} else {
			popupTitle = formatMessage(messages.reviewAccessNoScopeTitle);
			popupBody = formatMessage(messages.reviewAccessNoScopeDescription, {
				integration: product,
			});
			buttonTitle = formatMessage(messages.reviewAccessNoScope, {
				integration: product,
			});
		}
	}
	if (scopeState === ScopeState.PARTIAL_SCOPE) {
		popupTitle = formatMessage(messages.reviewAccessPartialAccessTitle, {
			integration: product,
			space,
		});
		popupBody = formatMessage(messages.reviewAccessPartialAccessDescription, {
			integration: product,
			space,
		});
		buttonTitle = formatMessage(messages.reviewAccessPartialScope, {
			integration: product,
		});
	}

	const toggleHover = useCallback(() => setHover(!hover), [hover]);

	const closePopup = useCallback(() => setShowPopup(false), []);

	const onUpdateClick = useCallback(() => {
		analytics.updateWarningClicked(integration, orgId || null, scopeState);
		setShowPopup(!showPopup);
	}, [integration, orgId, analytics, showPopup, scopeState]);

	const onDisconnectClick = useCallback(() => {
		onDisconnectProvider(integration);
	}, [integration, onDisconnectProvider]);

	useEffect(() => {
		analytics.updateConnectionShown(integration, scopeState);
	}, [integration, scopeState, analytics]);

	const handleContinueButton = useCallback(() => {
		if (onConnectProvider) {
			analytics.scopeUpdateButtonClicked(integration, scopeState);
			onConnectProvider({
				serviceKey: integration,
			});
		}
	}, [onConnectProvider, integration, scopeState, analytics]);

	const continueButton = (
		<Button appearance="link" spacing="none" onClick={handleContinueButton}>
			{formatMessage(messages.reviewAccessContinue)}
		</Button>
	);
	const disconnectButton = (
		<InstrumentedButton
			actionSubjectId="disconnectUpdate"
			analyticsAttributes={{
				integration,
				orgId,
				fromMenu: false,
			}}
			appearance="link"
			spacing="none"
			onClick={onDisconnectClick}
		>
			{formatMessage(messages.reviewAccessDisconnect)}
		</InstrumentedButton>
	);

	const notNowButton = (
		<InstrumentedButton
			actionSubjectId="notNowUpdate"
			analyticsAttributes={{ integration }}
			appearance="link"
			spacing="none"
			onClick={closePopup}
		>
			{formatMessage(messages.reviewAccessNotNow)}
		</InstrumentedButton>
	);

	const PopupContent = () => {
		return (
			<Stack xcss={styles.popupWrapper}>
				<Text weight="bold">{popupTitle}</Text>
				<Inline xcss={styles.popupBody}>{popupBody}</Inline>
				<Inline alignBlock="start" xcss={styles.popupButtonWrapper} space="space.050">
					{continueButton}
					<Text as="span">•</Text>
					{scopeState === ScopeState.NO_SCOPE ? disconnectButton : notNowButton}
				</Inline>
			</Stack>
		);
	};

	return (
		<Popup
			onClose={closePopup}
			isOpen={showPopup}
			content={() => <PopupContent />}
			trigger={(triggerProps) => (
				<Button
					{...triggerProps}
					appearance="subtle-link"
					spacing="none"
					onClick={onUpdateClick}
					onMouseEnter={toggleHover}
					onMouseLeave={toggleHover}
					iconBefore={
						scopeState === ScopeState.NO_SCOPE ? (
							<ErrorIcon
								label={'error'}
								color={showPopup || hover ? iconColor.error.accent : iconColor.error.color}
								spacing="spacious"
							/>
						) : (
							<WarningIcon
								label={'warning'}
								color={showPopup || hover ? iconColor.warning.accent : iconColor.warning.color}
								spacing="spacious"
							/>
						)
					}
					style={{
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						alignItems: 'center',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						fontWeight: token('font.weight.regular'),
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						alignSelf: 'flex-start',
					}}
				>
					<Inline as="span" xcss={styles.buttonTitle}>
						{buttonTitle}
					</Inline>
				</Button>
			)}
			offset={[0, 4]}
			zIndex={layers.flag()}
			placement="bottom-start"
		/>
	);
};

const UpdateConnection: React.FC<OwnProps> = ({ scope }) => {
	const { updateIntegrations } = useThirdPartyState();
	const updatableIntegrations: Nullable<Set<Integration>> = useMemo(() => {
		switch (scope) {
			case ScopeState.NO_SCOPE:
				return updateIntegrations.noScope;
			case ScopeState.PARTIAL_SCOPE:
				return updateIntegrations.partialScope;
			default:
				return null;
		}
	}, [scope, updateIntegrations]);

	return (
		<Stack xcss={styles.wrapper}>
			{updatableIntegrations &&
				[...updatableIntegrations].map((integration) => (
					<UpdateIntegration integration={integration} scopeState={scope} key={integration} />
				))}
		</Stack>
	);
};

export default UpdateConnection;
